import DashboardNav from "../components/DashboardNav/DashboardNav";
import ErrorModal from "../components/ErrorModal/ErrorModal";
import { UserAuth } from "../context/AuthContext";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Breadcrumb } from "react-bootstrap";
import "./dashboard.css";
import "./analytics.css";

const Analytics = () => {
  const [modalShow, setModalShow] = useState(true);
  const { user, roles } = UserAuth();
  const navigate = useNavigate();
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Analytics data states
  const [loading, setLoading] = useState(true);
  const [totalScans, setTotalScans] = useState(0);
  const [uniqueScans, setUniqueScans] = useState(0);
  const [activeQRCodes, setActiveQRCodes] = useState(0);
  const [topQRCodes, setTopQRCodes] = useState<
    Array<{ title: string; totalVisits: number }>
  >([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [scanData, setScanData] = useState<Record<string, number>>({});
  const [countryData, setCountryData] = useState<Record<string, number>>({});
  const [browserData, setBrowserData] = useState<Record<string, number>>({});
  const [osData, setOSData] = useState<Record<string, number>>({});
  const [isChartScrollable, setIsChartScrollable] = useState(false);

  // Date range for filtering data
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7); // Default to last 7 days
    return date.toISOString().split("T")[0];
  });

  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split("T")[0];
  });

  const [selectedDatePreset, setSelectedDatePreset] = useState("lastWeek");

  // State for global chart tooltip
  const [tooltipInfo, setTooltipInfo] = useState({
    visible: false,
    text: "",
    x: 0,
    y: 0,
  });

  // Find maximum scan value for scaling the chart
  const maxScans =
    Object.values(scanData).length > 0
      ? Math.max(...Object.values(scanData))
      : 0;

  // Set bar height scale factor (maximum pixel height / maximum scan value)
  const heightScale = maxScans > 0 ? 150 / maxScans : 0;

  // Add the check for scrollability
  useEffect(() => {
    if (chartContainerRef.current) {
      const container = chartContainerRef.current;
      setIsChartScrollable(container.scrollWidth > container.clientWidth);
    }
  }, [scanData]);

  useEffect(() => {
    // If user doesn't have premium or business plan, show modal and prevent analytics rendering
    if (!roles.premium && !roles.business) {
      setModalShow(true);
      return;
    }

    setModalShow(false);
    fetchAllData();
  }, [user, roles, startDate, endDate]);

  useEffect(() => {
    // Set date range based on selected preset
    const today = new Date();
    // Adjust for local timezone to ensure consistent date handling
    const localDate = new Date(
      today.getTime() - today.getTimezoneOffset() * 60000
    );
    const formattedToday = localDate.toISOString().split("T")[0];

    switch (selectedDatePreset) {
      case "today":
        setStartDate(formattedToday);
        setEndDate(formattedToday);
        break;
      case "yesterday": {
        const yesterday = new Date(localDate);
        yesterday.setDate(yesterday.getDate() - 1);
        const formattedYesterday = yesterday.toISOString().split("T")[0];
        setStartDate(formattedYesterday);
        setEndDate(formattedYesterday);
        break;
      }
      case "lastWeek": {
        const lastWeek = new Date(localDate);
        lastWeek.setDate(lastWeek.getDate() - 7);
        const formattedLastWeek = lastWeek.toISOString().split("T")[0];
        setStartDate(formattedLastWeek);
        setEndDate(formattedToday);
        break;
      }
      case "lastMonth": {
        const lastMonth = new Date(localDate);
        lastMonth.setDate(lastMonth.getDate() - 30);
        const formattedLastMonth = lastMonth.toISOString().split("T")[0];
        setStartDate(formattedLastMonth);
        setEndDate(formattedToday);
        break;
      }
      // Custom range is handled directly by date inputs
      default:
        break;
    }
  }, [selectedDatePreset]);

  const fetchAllData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/analytics/overview/${user.uid}/${startDate}/${endDate}`,
        {
          headers: {
            authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          // Set default values for all states
          setTotalScans(0);
          setUniqueScans(0);
          setActiveQRCodes(0);
          setTopQRCodes([]);
          setScanData({});
          setCountryData({});
          setBrowserData({});
          setOSData({});
          return;
        }
        throw new Error(
          `HTTP Error: ${response.status} - ${response.statusText}`
        );
      }

      const responseData = await response.json();

      // Update all states at once
      setTotalScans(responseData.data.overview.totalScans);
      setUniqueScans(responseData.data.overview.uniqueScans);
      setActiveQRCodes(responseData.data.overview.activeQRCodes);
      setTopQRCodes(responseData.data.topQRCodes);
      setScanData(responseData.data.dailyScans);
      setCountryData(responseData.data.demographics.countries);
      setBrowserData(responseData.data.demographics.browsers);
      setOSData(responseData.data.demographics.operatingSystems);
    } catch (error) {
      console.error("Error fetching analytics data:", error);
      // Set default values for all states
      setTotalScans(0);
      setUniqueScans(0);
      setActiveQRCodes(0);
      setTopQRCodes([]);
      setScanData({});
      setCountryData({});
      setBrowserData({});
      setOSData({});
    } finally {
      setLoading(false);
    }
  };

  const handleNoThanks = () => {
    navigate("/dashboard");
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
    setSelectedDatePreset("custom");
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
    setSelectedDatePreset("custom");
  };

  // Format date for display (e.g., "Mar 29")
  const formatDate = (dateString: string) => {
    // Create date in UTC to avoid timezone issues
    const date = new Date(dateString + "T00:00:00Z");
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  };

  // Helper function to get browser chart colors
  const browserColors = [
    "#8854d0",
    "#a55eea",
    "#cd84f1",
    "#c56cf0",
    "#7158e2",
    "#9b59b6",
    "#be2edd",
    "#5f27cd",
    "#341f97",
    "#4834d4",
  ];
  const getBrowserColor = (index: number) =>
    browserColors[index % browserColors.length];

  // Add color arrays for OS and Country charts with more unique colors
  const osColors = [
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#96CEB4",
    "#FFEEAD",
    "#FF9F9F",
    "#2ECC71",
    "#3498DB",
    "#9B59B6",
    "#34495E",
    "#16A085",
    "#27AE60",
    "#2980B9",
    "#8E44AD",
    "#2C3E50",
  ];
  const getOSColor = (index: number) => osColors[index % osColors.length];

  const countryColors = [
    "#6C5CE7",
    "#A8E6CF",
    "#DCEDC1",
    "#FFD3B6",
    "#FFAAA5",
    "#786FA6",
    "#596275",
    "#574B90",
    "#303952",
    "#1B9CFC",
    "#58B19F",
    "#9AECDB",
    "#FD7272",
    "#BDC581",
    "#45CE30",
  ];
  const getCountryColor = (index: number) =>
    countryColors[index % countryColors.length];

  // Helper function to generate country, browser and OS pie chart data
  const generateChartLegendItems = (data: Record<string, number>) => {
    // Calculate total for percentages
    const total = Object.values(data).reduce((sum, value) => sum + value, 0);
    if (total === 0) return [];

    // Convert to array of items with percentages
    return Object.entries(data)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({
        name,
        value,
        percentage: Math.round((value / total) * 100),
      }));
  };

  // Helper function to generate pie chart gradient
  const generatePieChartGradient = (
    items: Array<{ percentage: number }>,
    getColor: (index: number) => string
  ) => {
    let currentPercentage = 0;
    return `conic-gradient(${items
      .map((item, index) => {
        const start = currentPercentage;
        currentPercentage += item.percentage;
        return `${getColor(index)} ${start}% ${currentPercentage}%`;
      })
      .join(", ")})`;
  };

  // Don't render analytics content for free users
  if (!roles.premium && !roles.business) {
    return (
      <div className="analytics-page">
        <div className="analytics">
          <div className="dahsboard__menu">
            <DashboardNav />
          </div>
          <ErrorModal
            show={modalShow}
            onHide={handleNoThanks}
            backdrop="static"
            keyboard={false}
          />
        </div>
      </div>
    );
  }

  // Render analytics content for premium/business users
  return (
    <div className="analytics-page">
      <div className="analytics">
        <div className="dahsboard__menu">
          <DashboardNav />
        </div>
        <div className="analytics__container">
          <div className="analytics-header">
            <div className="analytics-header-left">
              <h1 className="analytics-title">Analytics Dashboard</h1>
              <Breadcrumb className="overview__container__breadcrumbs">
                <Breadcrumb.Item href="/home">Home</Breadcrumb.Item>
                <Breadcrumb.Item href="/dashboard">Dashboard</Breadcrumb.Item>
                <Breadcrumb.Item active>Analytics</Breadcrumb.Item>
              </Breadcrumb>
            </div>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading your analytics data...</p>
            </div>
          ) : (
            <>
              {/* Summary Metrics Section */}
              <div className="summary-metrics">
                <div className="metric-card">
                  <h3>Total Scans</h3>
                  <div className="metric-value">{totalScans}</div>
                </div>
                <div className="metric-card">
                  <h3>Unique Scans</h3>
                  <div className="metric-value">{uniqueScans}</div>
                </div>
                <div className="metric-card">
                  <h3>Active QR Codes</h3>
                  <div className="metric-value">{activeQRCodes}</div>
                </div>
              </div>

              {/* Date Range Selection */}
              <div className="date-filter-section">
                <div className="date-presets">
                  <select
                    className="date-preset-select"
                    value={selectedDatePreset}
                    onChange={(e) => setSelectedDatePreset(e.target.value)}
                  >
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="lastWeek">Last 7 days</option>
                    <option value="lastMonth">Last 30 days</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>
                <div className="date-range-inputs">
                  <input
                    type="date"
                    value={startDate}
                    onChange={handleStartDateChange}
                  />
                  <span>to</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={handleEndDateChange}
                  />
                </div>
              </div>

              {/* Data Grid - Charts and Tables */}
              <div className="data-grid">
                <div className="data-card statistics-card">
                  <h2>Scan Statistics</h2>
                  <div
                    className={`scan-chart ${
                      isChartScrollable ? "scrollable" : ""
                    }`}
                    ref={chartContainerRef}
                  >
                    {/* Global tooltip that's shared across all bars */}
                    <div
                      className="global-tooltip"
                      style={{
                        display: tooltipInfo.visible ? "block" : "none",
                        left: `${tooltipInfo.x}px`,
                        top: `${tooltipInfo.y}px`,
                      }}
                    >
                      {tooltipInfo.text}
                    </div>

                    {Object.keys(scanData).length > 0 ? (
                      <div
                        className="simple-bar-chart"
                        style={{
                          width: `${Math.max(
                            100,
                            Object.keys(scanData).length * 30
                          )}px`,
                          justifyContent:
                            Object.keys(scanData).length <= 10
                              ? "center"
                              : "flex-start",
                        }}
                      >
                        {Object.entries(scanData)
                          .sort(([dateA], [dateB]) => {
                            // Create dates in UTC to avoid timezone issues
                            const dateAObj = new Date(dateA + "T00:00:00Z");
                            const dateBObj = new Date(dateB + "T00:00:00Z");
                            return dateAObj.getTime() - dateBObj.getTime();
                          })
                          .map(([date, count], index) => {
                            // Calculate bar width based on number of days
                            const totalDays = Object.keys(scanData).length;
                            const barWidth =
                              totalDays <= 8 ? 40 : totalDays <= 15 ? 30 : 20;

                            // Determine if we should show the value inside the bar (for tall bars)
                            const barHeight = count * heightScale;
                            const showValueInside = barHeight > 40;

                            return (
                              <div
                                className="bar-container"
                                key={index}
                                style={{
                                  margin:
                                    totalDays <= 8
                                      ? "0 15px"
                                      : totalDays <= 15
                                      ? "0 10px"
                                      : "0 5px",
                                }}
                              >
                                <div
                                  className="bar"
                                  style={{
                                    height: `${barHeight}px`,
                                    minHeight: count > 0 ? "5px" : "1px",
                                    width: `${barWidth}px`,
                                  }}
                                  onMouseEnter={(e) => {
                                    const rect =
                                      e.currentTarget.getBoundingClientRect();
                                    const chartRect =
                                      chartContainerRef.current?.getBoundingClientRect() || {
                                        top: 0,
                                        left: 0,
                                      };

                                    // Position tooltip centered above the bar
                                    const x =
                                      rect.left -
                                      chartRect.left +
                                      rect.width / 2;

                                    // Position tooltip directly above the bar top
                                    const y = rect.top - chartRect.top - 30;

                                    setTooltipInfo({
                                      visible: true,
                                      text: `${formatDate(
                                        date
                                      )}: ${count} scan${
                                        count !== 1 ? "s" : ""
                                      }`,
                                      x,
                                      y,
                                    });
                                  }}
                                  onMouseLeave={() => {
                                    setTooltipInfo((prev) => ({
                                      ...prev,
                                      visible: false,
                                    }));
                                  }}
                                >
                                  {count > 0 && (
                                    <div
                                      className={`bar-value ${
                                        showValueInside ? "inside" : ""
                                      }`}
                                    >
                                      {count}
                                    </div>
                                  )}
                                </div>
                                <div className="bar-label">
                                  {formatDate(date)}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <div className="no-data-message">
                        No scan data available for the selected date range
                      </div>
                    )}
                  </div>
                </div>

                <div className="data-card top-codes-card">
                  <h2>QR Code Performance</h2>
                  <div className="table-filter">
                    <input
                      type="text"
                      placeholder="Search all QR codes..."
                      className="search-input"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <button className="filter-button">Search</button>
                  </div>
                  <div className="qr-table">
                    {topQRCodes.length > 0 ? (
                      <table>
                        <thead>
                          <tr>
                            <th>Page Name</th>
                            <th>Scans</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topQRCodes
                            .filter((code) =>
                              code.title
                                .toLowerCase()
                                .includes(searchTerm.toLowerCase())
                            )
                            .sort((a, b) => b.totalVisits - a.totalVisits)
                            .slice(0, searchTerm ? undefined : 4)
                            .map((code, index) => (
                              <tr
                                key={index}
                                className={
                                  code.totalVisits === 0 ? "inactive-row" : ""
                                }
                              >
                                <td>{code.title}</td>
                                <td className="scan-count">
                                  {code.totalVisits}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="no-data-message">
                        No QR code data available
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Demographic Analysis Section */}
              <div className="analysis-section">
                <h2>Demographic Analysis</h2>
                <div className="charts-container">
                  {/* Country Analysis */}
                  <div className="chart-grid">
                    <h3>Country Analysis</h3>
                    {Object.keys(countryData).length > 0 ? (
                      <div className="chart-container">
                        <div
                          className="pie-chart country-chart"
                          style={{
                            backgroundImage: generatePieChartGradient(
                              generateChartLegendItems(countryData),
                              getCountryColor
                            ),
                          }}
                        ></div>
                        <div className="chart-legend">
                          {generateChartLegendItems(countryData).map(
                            (item, index) => (
                              <div className="legend-item" key={index}>
                                <span
                                  className="legend-color"
                                  style={{
                                    backgroundColor: getCountryColor(index),
                                  }}
                                ></span>
                                <span className="legend-label">
                                  {item.name}: {item.percentage}%
                                </span>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="no-data-message">
                        No country data available
                      </div>
                    )}
                  </div>

                  {/* Browser Analysis */}
                  <div className="chart-grid">
                    <h3>Browser Analysis</h3>
                    {Object.keys(browserData).length > 0 ? (
                      <div className="chart-container">
                        <div
                          className="pie-chart browser-chart"
                          style={{
                            backgroundImage: generatePieChartGradient(
                              generateChartLegendItems(browserData),
                              getBrowserColor
                            ),
                          }}
                        ></div>
                        <div className="chart-legend">
                          {generateChartLegendItems(browserData).map(
                            (item, index) => (
                              <div className="legend-item" key={index}>
                                <span
                                  className="legend-color"
                                  style={{
                                    backgroundColor: getBrowserColor(index),
                                  }}
                                ></span>
                                <span className="legend-label">
                                  {item.name}: {item.percentage}%
                                </span>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="no-data-message">
                        No browser data available
                      </div>
                    )}
                  </div>

                  {/* OS Analysis */}
                  <div className="chart-grid">
                    <h3>OS Analysis</h3>
                    {Object.keys(osData).length > 0 ? (
                      <div className="chart-container">
                        <div
                          className="pie-chart os-chart"
                          style={{
                            backgroundImage: generatePieChartGradient(
                              generateChartLegendItems(osData),
                              getOSColor
                            ),
                          }}
                        ></div>
                        <div className="chart-legend">
                          {generateChartLegendItems(osData).map(
                            (item, index) => (
                              <div className="legend-item" key={index}>
                                <span
                                  className="legend-color"
                                  style={{
                                    backgroundColor: getOSColor(index),
                                  }}
                                ></span>
                                <span className="legend-label">
                                  {item.name}: {item.percentage}%
                                </span>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="no-data-message">
                        No OS data available
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
